import { useMemo } from "react";

import { notifications } from "@mantine/notifications";
import axios from "axios";

import { toLocalStackUrl } from "@/utils/upload/s3upload";
import { approveFiles } from "./fileApproval";
import { useFileManagerMount } from "./FileManagerMount";
import type {
  ApprovalCallbackPayload,
  CreateFolderCallbackPayload,
  DeleteCallbackPayload,
  FileManagerItem,
  FileManagerOperationCallbacks,
  FileManagerQueryContext,
  MoveCallbackPayload,
  RenameCallbackPayload,
  UpdateNoteCallbackPayload,
  UpdateTagsCallbackPayload,
  UploadFileItem,
} from "@/components/core/FileManager";

type OperationHandler<TPayload> = (
  payload: TPayload,
  ctx: FileManagerQueryContext,
  callbacks: FileManagerOperationCallbacks,
) => void | Promise<void>;

export interface FileOperations {
  fetchItems: (ctx: FileManagerQueryContext) => Promise<FileManagerItem[]>;
  onMoveItems?: OperationHandler<MoveCallbackPayload>;
  onCopyItems?: OperationHandler<MoveCallbackPayload>;
  onDeleteFiles?: OperationHandler<DeleteCallbackPayload>;
  onRename?: OperationHandler<RenameCallbackPayload>;
  onCreateFolder?: OperationHandler<CreateFolderCallbackPayload>;
  onUpload?: OperationHandler<UploadFileItem[]>;
  onApprove?: OperationHandler<ApprovalCallbackPayload>;
  onUpdateTags?: OperationHandler<UpdateTagsCallbackPayload>;
  onUpdateNote?: OperationHandler<UpdateNoteCallbackPayload>;
}

export function useFileOperations(): FileOperations {
  const {
    browseItems,
    updateDocuments,
    generateUploadUrls,
    invalidateScope,
    readOnly,
    approvalMode,
    allowRootUpload,
  } = useFileManagerMount();

  // The container's own refresh only covers its file-manager scope; invalidateScope also
  // reaches the Files-hub browse queries that render per-resource file counts. Every mutation
  // goes through here so no caller can forget one of the two.
  function succeed(callbacks: FileManagerOperationCallbacks) {
    invalidateScope();
    callbacks.onSuccess();
  }

  const fetchItems = useMemo(
    () =>
      (folderContext: FileManagerQueryContext): Promise<FileManagerItem[]> =>
        browseItems(folderContext.currentFolderId, folderContext.search),
    [browseItems],
  );

  function onMoveItems(
    movePayload: MoveCallbackPayload,
    _folderContext: FileManagerQueryContext,
    callbacks: FileManagerOperationCallbacks,
  ) {
    updateDocuments({
      action: "moveItems",
      itemIds: movePayload.items.map((item) => item.id),
      destinationParentId: movePayload.destination.id,
    })
      .then(() => {
        notifications.show({
          title: "Moved",
          message: `${movePayload.items.length} item${movePayload.items.length !== 1 ? "s" : ""} moved`,
          color: "blue",
          autoClose: 3000,
        });
        succeed(callbacks);
      })
      .catch(callbacks.onError);
  }

  function onCopyItems(
    copyPayload: MoveCallbackPayload,
    _folderContext: FileManagerQueryContext,
    callbacks: FileManagerOperationCallbacks,
  ) {
    updateDocuments({
      action: "copyItems",
      itemIds: copyPayload.items.map((item) => item.id),
      destinationParentId: copyPayload.destination.id,
      newName: copyPayload.items[0]?.newName,
      resolveConflict: true,
    })
      .then(() => {
        notifications.show({
          title: "Copied",
          message: `${copyPayload.items.length} item${copyPayload.items.length !== 1 ? "s" : ""} copied`,
          color: "blue",
          autoClose: 3000,
        });
        succeed(callbacks);
      })
      .catch(callbacks.onError);
  }

  function onDeleteFiles(
    deletePayload: DeleteCallbackPayload,
    _folderContext: FileManagerQueryContext,
    callbacks: FileManagerOperationCallbacks,
  ) {
    const fileIds = deletePayload.items
      .filter((item) => item.type === "FILE")
      .map((item) => item.id);
    const folderIds = deletePayload.items
      .filter((item) => item.type === "FOLDER")
      .map((item) => item.id);

    const tasks: Promise<unknown>[] = [];
    if (fileIds.length) {
      tasks.push(updateDocuments({ action: "deleteFiles", fileIds }));
    }
    folderIds.forEach((folderId) => {
      tasks.push(updateDocuments({ action: "deleteFolder", folderId }));
    });

    Promise.all(tasks)
      .then(() => {
        notifications.show({
          title: "Deleted",
          message: `${deletePayload.items.length} item${deletePayload.items.length !== 1 ? "s" : ""} deleted`,
          color: "red",
          autoClose: 3000,
        });
        succeed(callbacks);
      })
      .catch(callbacks.onError);
  }

  function onRename(
    renamePayload: RenameCallbackPayload,
    _folderContext: FileManagerQueryContext,
    callbacks: FileManagerOperationCallbacks,
  ) {
    const renameRequest =
      renamePayload.itemType === "FOLDER"
        ? updateDocuments({
            action: "renameFolder",
            folderId: renamePayload.id,
            newName: renamePayload.newName,
          })
        : updateDocuments({
            action: "renameFile",
            fileId: renamePayload.id,
            newName: renamePayload.newName,
          });

    renameRequest
      .then(() => {
        notifications.show({
          title: "Renamed",
          message: `"${renamePayload.currentName}" → "${renamePayload.newName}"`,
          color: "green",
          autoClose: 3000,
        });
        succeed(callbacks);
      })
      .catch(callbacks.onError);
  }

  function onCreateFolder(
    createFolderPayload: CreateFolderCallbackPayload,
    folderContext: FileManagerQueryContext,
    callbacks: FileManagerOperationCallbacks,
  ) {
    updateDocuments({
      action: "createFolder",
      folderName: createFolderPayload.name,
      parentId: folderContext.currentFolderId,
    })
      .then((data) => {
        if ((data as { conflict?: boolean } | undefined)?.conflict) {
          notifications.show({
            title: "Name already exists",
            message: `A folder named "${createFolderPayload.name}" already exists here.`,
            color: "red",
            autoClose: 4000,
          });
          callbacks.onError();
          return;
        }
        notifications.show({
          title: "Created",
          message: `Folder "${createFolderPayload.name}" created`,
          color: "green",
          autoClose: 3000,
        });
        succeed(callbacks);
      })
      .catch(callbacks.onError);
  }

  async function onUpload(
    uploadedFiles: UploadFileItem[],
    folderContext: FileManagerQueryContext,
    callbacks: FileManagerOperationCallbacks,
  ) {
    if (!folderContext.currentFolderId && !allowRootUpload) {
      notifications.show({
        title: "Select a folder",
        message: "Navigate into a folder before uploading files.",
        color: "yellow",
        autoClose: 4000,
      });
      callbacks.onError();
      return;
    }

    const uploadToastId = `wo-files-upload-${folderContext.currentFolderId}-${uploadedFiles.map((f) => f.name).join("-")}-${crypto.randomUUID()}`;
    notifications.show({
      id: uploadToastId,
      title: "Uploading files",
      message: `Uploading ${uploadedFiles.length} file${uploadedFiles.length !== 1 ? "s" : ""}…`,
      loading: true,
      autoClose: false,
      withCloseButton: false,
    });

    try {
      const uploads = await generateUploadUrls({
        action: "generateUploadUrls",
        parentId: folderContext.currentFolderId,
        files: uploadedFiles.map((f) => ({
          fileName: f.name,
          contentType: f.type || "application/octet-stream",
          fileSize: f.size,
          tags: f.tags?.length ? f.tags : undefined,
        })),
      });
      await Promise.all(
        uploads.map(({ uploadUrl }, index) => {
          const file = uploadedFiles[index];
          if (!file) return Promise.resolve();
          return axios.put(toLocalStackUrl(uploadUrl), file.file, {
            headers: { "Content-Type": file.type || "application/octet-stream" },
          });
        }),
      );
      notifications.update({
        id: uploadToastId,
        title: "Uploaded",
        message: `${uploadedFiles.length} file${uploadedFiles.length !== 1 ? "s" : ""} uploaded`,
        color: "green",
        loading: false,
        autoClose: 3000,
        withCloseButton: true,
      });
      succeed(callbacks);
    } catch (err) {
      notifications.update({
        id: uploadToastId,
        title: "Upload failed",
        message: "Something went wrong while uploading the files.",
        color: "red",
        loading: false,
        autoClose: 4000,
        withCloseButton: true,
      });
      // The backend creates the document row before handing back the presigned URL, so a
      // failed S3 PUT still leaves a row behind. Invalidate on the error path too: the orphan
      // then shows up right away rather than appearing out of nowhere on the next page load.
      invalidateScope();
      callbacks.onError(err);
    }
  }

  async function onApprove(
    approvalPayload: ApprovalCallbackPayload,
    ctx: FileManagerQueryContext,
    callbacks: FileManagerOperationCallbacks,
  ) {
    try {
      const { successMessage } = await approveFiles(
        approvalPayload,
        ctx,
        browseItems,
        updateDocuments,
      );
      notifications.show({
        title: "Marked for approval",
        message: successMessage,
        color: "green",
        autoClose: 3000,
      });
      succeed(callbacks);
    } catch (err) {
      if (err instanceof Error && err.message.includes("folder was not found")) {
        notifications.show({
          title: "Folder not found",
          message: err.message,
          color: "red",
          autoClose: 4000,
        });
      }
      callbacks.onError();
    }
  }

  function onUpdateTags(
    tagsPayload: UpdateTagsCallbackPayload,
    _folderContext: FileManagerQueryContext,
    callbacks: FileManagerOperationCallbacks,
  ) {
    updateDocuments({ action: "updateTags", itemId: tagsPayload.id, tags: tagsPayload.tags })
      .then(() => {
        notifications.show({
          title: "Tags updated",
          message: `Tags saved for "${tagsPayload.name}"`,
          color: "green",
          autoClose: 3000,
        });
        succeed(callbacks);
      })
      .catch(callbacks.onError);
  }

  function onUpdateNote(
    notePayload: UpdateNoteCallbackPayload,
    _folderContext: FileManagerQueryContext,
    callbacks: FileManagerOperationCallbacks,
  ) {
    updateDocuments({ action: "updateNote", itemId: notePayload.id, note: notePayload.note })
      .then(() => {
        callbacks.onSuccess();
      })
      .catch(callbacks.onError);
  }

  if (readOnly) {
    return { fetchItems };
  }

  return {
    fetchItems,
    onMoveItems,
    onCopyItems,
    onDeleteFiles,
    onRename,
    onCreateFolder,
    onUpload,
    onApprove: approvalMode ? onApprove : undefined,
    onUpdateTags,
    onUpdateNote,
  };
}
